'use strict';
import {Component, OnInit, Input, Inject} from '@angular/core';
import {BsModalRef} from 'ngx-bootstrap/modal';
import {Asset, AssetType, Assets, Brands, CtaType, FeatureCopy, ProductCopy, pageTypes} from '@ox/typings';
import {AssetsService, CopyFormattingService, ShopDataService} from '@webu/data';
import {Subject, combineLatest, filter, map, take, takeUntil} from 'rxjs';
import {AutoDestroy, BRAND, buildTrackingString} from '@ox/utils';

@Component({
  selector: 'webu-about-plan-modal',
  templateUrl: './about-plan-modal.component.html'
})
export class AboutPlanModalComponent implements OnInit {
  @Input() planName: string;
  @Input() features: FeatureCopy[];
  @Input() billingCadence: string;
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  productCopy?: ProductCopy;
  eligibleFeatures?: FeatureCopy[];
  isError = false;
  isLoading = true;
  planImageAsset?: Asset;
  selectedIndex?: number;
  
  //Tracking imports
  trackingString = (cta: CtaType, args: string[]) => buildTrackingString(pageTypes.myPlans, cta, args);

  assets$ = this.assetsService.getAssets().pipe(
    takeUntil(this.destroy$),
    filter((assets): assets is Assets => !!assets));


  constructor(
    private assetsService: AssetsService,
    private shopDataService: ShopDataService,
    public bsModalRef: BsModalRef,
    private copyFormattingService: CopyFormattingService,
    @Inject(BRAND) public brand: Brands
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    combineLatest([this.getCopy(), this.assets$])
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: () => {
        this.getPlanAssets();
        this.isLoading = false;
      },
      error: (err) => this.handleError(err)
  });
  }

  getPlanAssets() {
    if (this.productCopy && this.productCopy.code)
      this.planImageAsset = this.assetsService.getSpecificPlanAssetByType(this.productCopy.code, 'about.the.plan', AssetType.Image);
    else
      this.planImageAsset = this.assetsService.getSpecificPlanAssetByType(this.planName, 'about.the.plan', AssetType.Image);

    // Fallback for specific plans that are not in the asset file, ie "Connected Access" and most data plans
    if (!this.planImageAsset) 
      this.planImageAsset = this.assetsService.getSpecificPlanAssetByType(this.features.find((feat) => feat.featureCategoryCode)?.featureCategoryCode ?? '', 'about.the.plan', AssetType.Image);
  }

  getCopy() {
    return this.shopDataService.getCopyData().pipe(
      take(1),
      map((copy) => {
        this.productCopy = this.copyFormattingService.getProductCopyByName(copy, this.planName);
      })
    );
  }

  handleError(err: any) {
    // TODO: Add error state
    console.error(err);
  }

  getFeatureAsset(featureCode: string) {
    let asset = this.assetsService.getSpecificFeatureAssetByType(featureCode, 'about.the.plan.modal', AssetType.Image, this.brand);
    // If an asset is not found, try lookup with a specific feature category code
    if (!asset) {
      const featureCategoryCode = this.productCopy?.features.find(feat => feat.code === featureCode)?.featureCategoryCode ?? '';
      asset = this.assetsService.getSpecificFeatureAssetByType(featureCategoryCode, 'about.the.plan.modal', AssetType.Image, this.brand);
    }
    return asset;
  }

  selectFeature(index: number) {
    this.selectedIndex = index;
  }

  selectBackButton() {
    this.selectedIndex = undefined;
  }
}